import React from "react";
import { AGENT_MARKS } from "./agent-marks";

/**
 * One AI client's logo, at a fixed 16px, in the brand's own colours.
 *
 * Theme handling is a CSS custom property rather than a prop: the dark-theme
 * colour has to change with `data-theme`, and an inline `fill` cannot. The
 * element publishes both values and connect-mcp-modal.css picks one.
 *
 * Falls back to a monogram for the two clients with no obtainable mark, rather
 * than to a generic glyph: a row of identical placeholder icons reads as a
 * rendering bug, whereas "VS" plainly means VS Code. See agent-marks.ts for
 * where each asset comes from.
 */
export function AgentMark({ mark }: { mark: string }): React.ReactElement {
  const entry = AGENT_MARKS[mark];

  if (entry?.img) {
    return <img className="agentImg" src={entry.img} alt="" aria-hidden="true" />;
  }

  if (!entry?.d) {
    return (
      <span className="agentMono" aria-hidden="true">
        {entry?.monogram ?? "?"}
      </span>
    );
  }

  // Unique per mark: two gradients with the same id on one page collide, and
  // this dialog renders the same client in a tile and in its detail card.
  const gradientId = `agent-grad-${mark}`;

  const style = {
    "--agent-mark": entry.color ?? "currentColor",
    "--agent-mark-dark": entry.colorDark ?? entry.color ?? "currentColor",
  } as React.CSSProperties;

  return (
    <svg
      className="agentSvg"
      viewBox={entry.viewBox ?? "0 0 24 24"}
      width="16"
      height="16"
      style={style}
      aria-hidden="true"
    >
      {entry.gradient && (
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            {entry.gradient.map((stop, i) => (
              <stop
                key={stop}
                offset={`${(i / (entry.gradient!.length - 1)) * 100}%`}
                stopColor={stop}
              />
            ))}
          </linearGradient>
        </defs>
      )}
      <path fill={entry.gradient ? `url(#${gradientId})` : undefined} d={entry.d} />
    </svg>
  );
}
